import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Alert, Reveal, Skeleton, useIsMobile } from '../components/ui';
import AdminLayout from '../components/layout/AdminLayout';
import api from '../utils/api';

export default function AdminOrgs() {
  const [orgs, setOrgs] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('unverified');
  const [actioningId, setActioningId] = useState(null);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setOrgs(null);
    setLoadError(false);
    const params = filter === 'all' ? {} : { verified: filter === 'verified' };
    api.get('/admin/organizations', { params })
      .then(r => setOrgs(r.data))
      .catch(() => setLoadError(true));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggleVerify = async (org) => {
    setActioningId(org.id);
    try {
      await api.post(`/admin/organizations/${org.id}/${org.is_verified ? 'unverify' : 'verify'}`);
      load();
    } catch {
      // Reload will surface current state either way.
      load();
    } finally {
      setActioningId(null);
    }
  };

  return (
    <AdminLayout>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>Organizations</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Verify organizations so they can create donation camps.</p>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[['unverified', 'Pending'], ['verified', 'Verified'], ['all', 'All']].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            style={{
              padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              border: `1px solid ${filter === value ? 'var(--red)' : 'var(--gray-200)'}`,
              background: filter === value ? 'var(--red-light)' : 'white',
              color: filter === value ? 'var(--red-dark)' : 'var(--gray-600)',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <Card>
        {orgs === null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 0' }}>
                <div style={{ flex: 1 }}>
                  <Skeleton width="40%" height="13px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="55%" height="11px" />
                </div>
                <Skeleton width="70px" height="30px" radius="8px" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <Alert variant="error">Couldn't load organizations. Please try again.</Alert>
        ) : orgs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            <p style={{ fontSize: '13.5px' }}>Nothing here.</p>
          </div>
        ) : orgs.map((org, i) => (
          <Reveal key={org.id} delay={Math.min(i, 6) * 40}>
            <div
              style={{
                display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px',
                padding: '13px 0',
                borderBottom: i < orgs.length - 1 ? '1px solid var(--gray-100)' : 'none',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>{org.name}</span>
                  <Badge color={org.is_verified ? 'green' : 'yellow'}>{org.is_verified ? 'Verified' : 'Pending'}</Badge>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                  {[org.org_type, org.city, org.registration_number].filter(Boolean).join(' · ') || 'No additional details'}
                </div>
              </div>
              <Button
                size="sm"
                variant={org.is_verified ? 'secondary' : 'primary'}
                loading={actioningId === org.id}
                onClick={() => toggleVerify(org)}
              >
                {org.is_verified ? 'Revoke' : 'Verify'}
              </Button>
            </div>
          </Reveal>
        ))}
      </Card>
    </AdminLayout>
  );
}
